'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useMeQuery } from '@frameio/graphql-client/generated';
import { useGetUxpHostInfoQuery } from '@frameio/integrations/shared/gql/queries/generated/GetUxpHostInfo';
import { useIsSegmentLoaded } from '@frameio/segment/contexts';
import { useActiveAccountId } from '@frameio/shared/contexts';
import {
  useAnalytics,
  useGroupCallOnAccountLoad,
  useTrack,
} from '@frameio/shared/segment/hooks';
import type { MeForAnalytics } from '@frameio/shared/segment/hooks/useAnalytics';
import {
  setUxpHostInfo,
  useCoreOptionsForAnalytics,
} from '@frameio/shared/segment/hooks/useCoreOptionsForAnalytics';
import { getPageTypeWithSettingsSections } from '@frameio/shared/segment/hooks/utils/getPageTypeWithSettingsSections';
import { trackPageView } from '@frameio/shared/segment/utils';
import { isUXPEnvironment } from '@frameio/utils/config';
import { usePrevious } from '@frameio/vapor';

function uxpHostNameForAnalytics(name: string): string {
  return name === 'premierepro' ? 'premiere' : name;
}

const useTrackAccountLoaded = (me: MeForAnalytics) => {
  const accountId = useActiveAccountId();
  const previousAccountId = usePrevious(accountId);
  const track = useTrack();

  useEffect(() => {
    if (me && accountId && previousAccountId !== accountId) {
      const isAccountSwitched =
        previousAccountId && previousAccountId !== accountId;

      track('account-loaded', {
        is_account_switched: !!isAccountSwitched,
      });
    }
  }, [accountId, me, previousAccountId, track]);
};

const useTrackPageViews = (isEnabled: boolean, me: MeForAnalytics) => {
  const pathname = usePathname();
  const { coreOptions } = useCoreOptionsForAnalytics(me?.isEdu);

  useEffect(
    function trackPageViewOnPathnameChanged() {
      if (!isEnabled || !me) return;
      try {
        const context = coreOptions()?.context;
        trackPageView(getPageTypeWithSettingsSections(pathname), context);
      } catch (e) {
        // Prevent this error from bubbling up to an error boundary
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEnabled, pathname, me]
  );
};

type MainAppAnalyticsProps = {
  skipGroupCallOnAccountLoad?: boolean;
};

export function MainAppAnalytics({
  skipGroupCallOnAccountLoad = false,
}: MainAppAnalyticsProps = {}) {
  const { isSegmentLoaded } = useIsSegmentLoaded();
  const { data: user } = useMeQuery({
    skip: !isSegmentLoaded,
    context: {
      // Avoid redirecting to login on this call (e.g. shares)
      shouldRedirectToLoginIfUnauthenticated: false,
      bypassShareAuth: true,
    },
    fetchPolicy: 'cache-only',
  });
  const accountId = useActiveAccountId();
  // should i just shape this correctly by pulling out the fields?
  const me = user?.me as MeForAnalytics;

  const uxpHostInfo = useGetUxpHostInfoQuery({
    context: { resolveWithUXP: true },
    skip: !isUXPEnvironment(),
  })?.data?.uxpHostInfo;

  const analyticsHostName = uxpHostInfo
    ? uxpHostNameForAnalytics(uxpHostInfo.name)
    : undefined;

  // Store UXP host info globally for use in analytics
  useEffect(() => {
    if (!uxpHostInfo) return;
    setUxpHostInfo({
      name: analyticsHostName,
      hostVersion: uxpHostInfo.version,
      pluginVersion: uxpHostInfo.pluginVersion,
      version: uxpHostInfo.uxpVersion,
      schemaVersion: uxpHostInfo.schemaVersion,
      uiLocale: uxpHostInfo.uiLocale,
    });
  }, [analyticsHostName, uxpHostInfo]);

  const options = uxpHostInfo
    ? {
        buildInfo: {
          name: analyticsHostName,
          hostVersion: uxpHostInfo.version,
          pluginVersion: uxpHostInfo.pluginVersion,
          version: uxpHostInfo.uxpVersion,
          schemaVersion: uxpHostInfo.schemaVersion,
          uiLocale: uxpHostInfo.uiLocale,
        },
        identifyOptions: {
          accountId,
        },
      }
    : undefined;

  useAnalytics({
    me,
    pageViewIdentifier: undefined,
    options,
  });
  useTrackAccountLoaded(me);
  useGroupCallOnAccountLoad(skipGroupCallOnAccountLoad);
  useTrackPageViews(isSegmentLoaded, me);

  return null;
}
